import {
  copyDiscussions,
  createBulkDiscussions,
  createDiscussion,
  deleteDiscussion,
  fetchDiscussionById,
  fetchDiscussions,
  followTrack,
  getNotesUnread,
  getTrackFollow,
  markNotesViewed,
  toggleDiscussionReaction,
  updateDiscussion,
} from '@disco/api/discussions'
import type {
  BulkCreateDiscussionRequest,
  BulkCreateDiscussionResponse,
  CopyDiscussionsRequest,
  CopyDiscussionsResponse,
  CreateDiscussionRequest,
  Discussion,
  DiscussionId,
  DiscussionUser,
  FollowTrackRequest,
  FollowTrackResponse,
  ListDiscussionsParams,
  NotesViewRequest,
  NotesViewResponse,
  PaginatedDiscussions,
  ReactDiscussionResponse,
  TrackFollowState,
  UpdateDiscussionRequest,
} from 'types/discussion'
import type { State } from 'types/redux'
import type { TrackId } from 'types/track'
import type { User } from 'types/user'

import { api } from './index'

const discussionListTagId = (
  target_type: string,
  target_object_id: number | string,
): string => `LIST-${target_type}-${target_object_id}`

/**
 * Builds the minimal `DiscussionUser` shape the BE returns on each record
 * from whatever's in `state.user`. Used to populate `reactions[].user`
 * during optimistic patches.
 */
const buildCurrentDiscussionUser = (state: State): DiscussionUser | null => {
  const user: User | undefined = state.user
  if (!user || user.id == null) return null
  return {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
  }
}

/**
 * Locates and patches a discussion inside a cached list response.
 * Based off maximum 1 level deep reply structure.
 */
export const patchDiscussionInList = (
  results: Discussion[],
  id: DiscussionId,
  parent: DiscussionId | null,
  patch: (draft: Discussion) => void,
): void => {
  if (parent === null) {
    const target = results.find(d => d.id === id)
    if (target) patch(target)
    return
  }
  const top = results.find(d => d.id === parent)
  const reply = top?.replies?.find(r => r.id === id)
  if (reply) patch(reply)
}

export const discussionsApi = api
  .enhanceEndpoints({
    addTagTypes: ['Discussions', 'NotesUnread', 'TrackFollow'],
  })
  .injectEndpoints({
    endpoints: build => ({
      getDiscussions: build.query<PaginatedDiscussions, ListDiscussionsParams>({
        queryFn: async params => {
          try {
            const data = await fetchDiscussions(params)
            return { data }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, { target_type, target_object_id }) => [
          {
            type: 'Discussions',
            id: discussionListTagId(target_type, target_object_id),
          },
        ],
      }),

      getDiscussionById: build.query<Discussion, DiscussionId>({
        queryFn: async id => {
          try {
            const data = await fetchDiscussionById(id)
            return { data }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, id) => [{ type: 'Discussions', id }],
      }),

      createDiscussion: build.mutation<Discussion, CreateDiscussionRequest>({
        queryFn: async data => {
          try {
            const result = await createDiscussion(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (
          _result,
          _error,
          { target_type, target_object_id, parent },
        ) => {
          const tags: {
            type: 'Discussions' | 'TrackFollow'
            id: string | number
          }[] = [
            {
              type: 'Discussions',
              id: discussionListTagId(target_type, target_object_id),
            },
          ]

          if (parent != null) tags.push({ type: 'Discussions', id: parent })
          // Writing a note/reply on a track can auto-follow the user, so refetch
          // the follow state to update the bell without a refresh.
          if (target_type === 'track') {
            tags.push({ type: 'TrackFollow', id: target_object_id })
          }
          return tags
        },
      }),

      getTrackFollow: build.query<
        TrackFollowState,
        FollowTrackRequest['target_object_id']
      >({
        queryFn: async trackId => {
          try {
            return { data: await getTrackFollow(trackId) }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, trackId) => [
          { type: 'TrackFollow', id: trackId },
        ],
      }),

      followTrack: build.mutation<FollowTrackResponse, FollowTrackRequest>({
        queryFn: async data => {
          try {
            const result = await followTrack(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        // Refetch on success AND failure so the server value wins: RTK keeps the
        // latest refetch, so out-of-order failures resolve without a manual guard
        // (inverse patches don't compose).
        invalidatesTags: (_result, _error, { target_object_id }) => [
          { type: 'TrackFollow', id: target_object_id },
        ],
        async onQueryStarted(
          { target_object_id, level },
          { dispatch, queryFulfilled },
        ) {
          dispatch(
            discussionsApi.util.updateQueryData(
              'getTrackFollow',
              target_object_id,
              draft => {
                if (!draft) return
                draft.level = level
                draft.source = 'manual'
              },
            ),
          )
          try {
            await queryFulfilled
          } catch {
            // No undo: the invalidation refetch is the source of truth.
          }
        },
      }),

      // Keyed by a single track id; the api layer still batches (GET
      // ?track_ids=...) so a future list view can fetch many rows at once.
      getNotesUnread: build.query<boolean, TrackId>({
        queryFn: async trackId => {
          try {
            const unread = await getNotesUnread([trackId])
            return { data: Boolean(unread[String(trackId)]) }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, trackId) => [
          { type: 'NotesUnread', id: trackId },
        ],
      }),

      markNotesViewed: build.mutation<NotesViewResponse, NotesViewRequest>({
        queryFn: async data => {
          try {
            return { data: await markNotesViewed(data) }
          } catch (error) {
            return { error }
          }
        },
        // Refetch after a successful view so a slower unread GET that was already
        // in flight can't resolve later and revive the dot (RTK keeps the newer).
        invalidatesTags: (_result, error, { track_id }) =>
          error ? [] : [{ type: 'NotesUnread', id: track_id }],
        async onQueryStarted({ track_id }, { dispatch, queryFulfilled }) {
          const patch = dispatch(setTrackNotesUnread(track_id, false))
          try {
            await queryFulfilled
          } catch {
            patch.undo()
          }
        },
      }),

      createBulkDiscussions: build.mutation<
        BulkCreateDiscussionResponse,
        BulkCreateDiscussionRequest
      >({
        queryFn: async data => {
          try {
            const result = await createBulkDiscussions(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { targets }) =>
          targets.map(({ target_type, target_object_id }) => ({
            type: 'Discussions' as const,
            id: discussionListTagId(target_type, target_object_id),
          })),
      }),

      copyDiscussions: build.mutation<
        CopyDiscussionsResponse,
        CopyDiscussionsRequest
      >({
        queryFn: async data => {
          try {
            const result = await copyDiscussions(data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { targets }) =>
          targets.map(({ target_type, target_object_id }) => ({
            type: 'Discussions' as const,
            id: discussionListTagId(target_type, target_object_id),
          })),
      }),

      updateDiscussion: build.mutation<
        Discussion,
        {
          id: DiscussionId
          data: UpdateDiscussionRequest
          target_type: string
          target_object_id: number | string
        }
      >({
        queryFn: async ({ id, data }) => {
          try {
            const result = await updateDiscussion(id, data)
            return { data: result }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (
          _result,
          _error,
          { id, target_type, target_object_id },
        ) => [
          { type: 'Discussions', id },
          {
            type: 'Discussions',
            id: discussionListTagId(target_type, target_object_id),
          },
        ],
      }),

      deleteDiscussion: build.mutation<
        null,
        {
          id: DiscussionId
          parent: DiscussionId | null
          target_type: string
          target_object_id: number | string
        }
      >({
        queryFn: async ({ id }) => {
          try {
            await deleteDiscussion(id)
            return { data: null }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'Discussions', id },
        ],
        async onQueryStarted(
          { id, parent, target_type, target_object_id },
          { dispatch, getState, queryFulfilled },
        ) {
          const cachedListQueries = discussionsApi.util
            .selectInvalidatedBy(getState(), [
              {
                type: 'Discussions',
                id: discussionListTagId(target_type, target_object_id),
              },
            ])
            .map(entry => entry.originalArgs as ListDiscussionsParams)

          const patchResults = [
            ...(parent != null
              ? [
                  dispatch(
                    discussionsApi.util.updateQueryData(
                      'getDiscussionById',
                      parent,
                      draft => {
                        if (draft.replies) {
                          draft.replies = draft.replies.filter(r => r.id !== id)
                        }
                        if (draft.replies_count > 0) draft.replies_count -= 1
                      },
                    ),
                  ),
                ]
              : []),
            ...cachedListQueries.map(originalArgs =>
              dispatch(
                discussionsApi.util.updateQueryData(
                  'getDiscussions',
                  originalArgs,
                  draft => {
                    if (parent == null) {
                      draft.results = draft.results.filter(d => d.id !== id)
                      return
                    }
                    const top = draft.results.find(d => d.id === parent)
                    if (!top) return
                    if (top.replies) {
                      top.replies = top.replies.filter(r => r.id !== id)
                    }
                    if (top.replies_count > 0) top.replies_count -= 1
                  },
                ),
              ),
            ),
          ]

          try {
            await queryFulfilled
          } catch {
            patchResults.forEach(p => p.undo())
          }
        },
      }),

      toggleDiscussionReaction: build.mutation<
        ReactDiscussionResponse,
        {
          id: DiscussionId
          parent: DiscussionId | null
          target_type: string
          target_object_id: number | string
          type: string
        }
      >({
        queryFn: async ({ id, type }) => {
          try {
            const data = await toggleDiscussionReaction(id, { type })
            return { data }
          } catch (error) {
            return { error }
          }
        },
        async onQueryStarted(
          { id, parent, target_type, target_object_id, type },
          { dispatch, getState, queryFulfilled },
        ) {
          // Optimistically toggle the current user reaction

          const currentUser = buildCurrentDiscussionUser(getState())
          if (!currentUser) {
            try {
              await queryFulfilled
            } catch {
              // No optimistic patch to undo.
            }
            return
          }

          const togglePatch = (draft: Discussion) => {
            const idx = draft.reactions.findIndex(
              r => r.type === type && r.user.id === currentUser.id,
            )
            if (idx >= 0) {
              draft.reactions.splice(idx, 1)
            } else {
              draft.reactions.push({ type, user: currentUser })
            }
          }

          const cachedListQueries = discussionsApi.util
            .selectInvalidatedBy(getState(), [
              {
                type: 'Discussions',
                id: discussionListTagId(target_type, target_object_id),
              },
            ])
            .map(entry => entry.originalArgs as ListDiscussionsParams)

          const patchResults = [
            dispatch(
              discussionsApi.util.updateQueryData(
                'getDiscussionById',
                id,
                draft => {
                  togglePatch(draft)
                },
              ),
            ),
            ...cachedListQueries.map(originalArgs =>
              dispatch(
                discussionsApi.util.updateQueryData(
                  'getDiscussions',
                  originalArgs,
                  draft => {
                    patchDiscussionInList(
                      draft.results,
                      id,
                      parent,
                      togglePatch,
                    )
                  },
                ),
              ),
            ),
          ]

          try {
            await queryFulfilled
          } catch {
            patchResults.forEach(p => p.undo())
          }
        },
      }),
    }),
  })

/**
 * Lights (or clears) a track's notes dot in the local cache without a fetch.
 * Used by the mention/reply websocket events to reflect new activity immediately.
 */
export const setTrackNotesUnread = (trackId: TrackId, unread: boolean) =>
  discussionsApi.util.updateQueryData('getNotesUnread', trackId, () => unread)

export const {
  useGetDiscussionsQuery,
  useGetDiscussionByIdQuery,
  useCreateDiscussionMutation,
  useCreateBulkDiscussionsMutation,
  useCopyDiscussionsMutation,
  useUpdateDiscussionMutation,
  useDeleteDiscussionMutation,
  useToggleDiscussionReactionMutation,
  useFollowTrackMutation,
  useGetTrackFollowQuery,
  useGetNotesUnreadQuery,
  useMarkNotesViewedMutation,
} = discussionsApi
